import { ref } from 'vue'
import { auth } from '@/store/authStore'
import userOperations from '@/composables/userOperations';
const { checkUserinfo } = userOperations()

// Shared state variables preserved in memory across your entire Vue architecture
const isAdmin = ref(false)
const lastChecked = ref(null)
const isChecking = ref(false)

function useAdminCheck() {

  /**
   * Evaluates if the current user holds active administration privileges.
   * @param {boolean} force - If true, bypasses the 1-hour cache buffer.
   */
  async function checkAdminStatus(force = false) {
    // Prevent overlapping simultaneous API calls
    if (isChecking.value) return isAdmin.value

    const ONE_HOUR = 60 * 60 * 1000
    const now = Date.now()

    // 💡 CACHE BUFFER: Skip the API trip if verified less than 1 hour ago
    if (!force && lastChecked.value && (now - lastChecked.value < ONE_HOUR)) {
      console.log('⏳ [Admin Check]: Using cached roles. Verified within the hour.')
      return isAdmin.value
    }

    isChecking.value = true

    console.log('🔍 [Admin Check]: Requesting authentic security claims token...')
      
      // MSAL cleanly grabs the token silently or prompts if expired
    checkUserinfo()
        .then(data => {
            console.log('✅ [Admin Check]: Received role data from server:', data)
            if (!data || typeof data.dB_isAdmin === 'undefined') {
              isAdmin.value = false 
              auth.syncUserProfile(null)
            } else {
              isAdmin.value = data.dB_isAdmin === 1
              
              auth.syncUserProfile(data)

              lastChecked.value = now
              console.log(`✅ [Admin Check]: Complete. Admin privileges: ${isAdmin.value}`)
            }
            
        })
        .catch(err => {
            console.error('❌ [Admin Check]: Authorization lookup failed:', err)
            // Safety First: Downgrade permissions if the security check breaks or fails
            isAdmin.value = false 
        })
        .finally(() => {
            isChecking.value = false
            return isAdmin.value
        })

  }

  /**
   * Handler for browser visibility changes (Tab switched back to active)
   */
  function handleVisibilityWakeup() {
    if (document.visibilityState === 'visible') {
      console.log('👀 [App Lifecycle]: Tab became active. Evaluating token freshness...')
      checkAdminStatus(false) // Will only hit the API if the 1-hour cache has expired
    }
  }

  /**
   * Attaches low-level browser lifecycle listeners to hook into the system layout.
   */
  function listen() {
    document.addEventListener('visibilitychange', handleVisibilityWakeup)
    // Run an initial evaluation immediately upon activation
    checkAdminStatus(false)
  }

  /**
   * Completely tears down state properties and security handles.
   */
  function destroy() {
    document.removeEventListener('visibilitychange', handleVisibilityWakeup)
    lastChecked.value = null
    isAdmin.value = false
  }

  return {
    isAdmin,
    isChecking,
    lastChecked,
    checkAdminStatus,
    listen,
    destroy
  }
}
export {useAdminCheck}